// TanStack Query hooks for storage objects, uploads, and the job queue.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { JobList, StorageObjectList, UploadResponse } from "./types";

export function useStorageObjects(prefix?: string) {
  return useQuery({
    queryKey: ["storage", prefix ?? ""],
    queryFn: () =>
      apiFetch<StorageObjectList>(
        `/api/storage/objects${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ""}`,
      ),
  });
}

export function useDeleteObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      apiFetch<void>(`/api/storage/objects/${key.split("/").map(encodeURIComponent).join("/")}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["storage"] }),
  });
}

export function useUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) =>
      apiFetch<UploadResponse>(`/api/uploads`, { method: "POST", body: form }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["storage"] });
      qc.invalidateQueries({ queryKey: ["recordings"] });
    },
  });
}

// Single self-describing combined bundle: <platform>_<org>_<uid>_<logId>-<username>.zip
// (video + CSVs + metadata.json). Handles both Mode 1 and Mode 2 recordings server-side.
export function useUploadZip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) =>
      apiFetch<UploadResponse>(`/api/uploads/zip`, { method: "POST", body: form }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["storage"] });
      qc.invalidateQueries({ queryKey: ["recordings"] });
    },
  });
}

export function useJobs(status?: string, enabled = true) {
  return useQuery({
    queryKey: ["jobs", status ?? ""],
    queryFn: () =>
      apiFetch<JobList>(`/api/jobs${status ? `?status=${encodeURIComponent(status)}` : ""}`),
    refetchInterval: 3000,
    enabled,
  });
}
